import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import {
  getBackwardTrace,
  getBatchSuggestions,
  BackwardTraceResponse,
} from '@/services/traceabilityService';

export default function BackwardTracePage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuery, setSelectedQuery] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [traceData, setTraceData] = useState<BackwardTraceResponse['data']>(null);
  const [isInvoiceSearch, setIsInvoiceSearch] = useState<boolean>(false);

  useEffect(() => {
    async function loadSuggestions() {
      try {
        const res = await getBatchSuggestions('backward', searchTerm);
        if (res && res.success) {
          setSuggestions(res.data || []);
        }
      } catch (err) {
        console.error('Failed to load batch suggestions:', err);
      }
    }
    loadSuggestions();
  }, [searchTerm]);

  const handleSearch = async (query?: string) => {
    const target = (query || searchTerm).trim();
    if (!target) return;

    setSelectedQuery(target);
    setShowSuggestions(false);
    setLoading(true);

    try {
      const res = await getBackwardTrace(target);
      if (res && res.success && res.found && res.data) {
        setTraceData(res.data);
        setIsInvoiceSearch(!!res.isInvoiceSearch);
      } else {
        setTraceData(null);
        setIsInvoiceSearch(false);
      }
    } catch (err) {
      console.error('Failed to get backward trace:', err);
      setTraceData(null);
      setIsInvoiceSearch(false);
    } finally {
      setLoading(false);
    }
  };

  const invoiceBatches = traceData?.batches || [];
  const directLinks = traceData?.links || [];
  const customers = traceData?.customers || [];

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/manufacturing/traceability')}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 cursor-pointer shadow-sm transition-colors"
              title="Back to Batch Traceability"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#1e293b]">Backward Batch Trace</h1>
              <p className="text-sm text-[#64748b] mt-0.5">
                Start from a Customer Invoice or FG Batch to trace backward to PO, Raw Materials, GRN, and Suppliers
              </p>
            </div>
          </div>

          {/* Search Section */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
            <label className="block text-sm font-bold text-[#1e293b] mb-2">
              Enter Sales Invoice Number or Finished Goods Batch Number
            </label>
            <div className="flex items-stretch gap-3">
              <div className="flex-1 relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                  <i className="ri-search-line text-base" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  placeholder="e.g. INV-2026-0001, B-PRD-2026-0001, BT-PX-001..."
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 font-medium"
                />
                {showSuggestions && searchTerm && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto">
                    {suggestions.map((b) => (
                      <div
                        key={b}
                        onMouseDown={() => {
                          setSearchTerm(b);
                          handleSearch(b);
                        }}
                        className="px-4 py-2.5 text-sm text-[#1e293b] hover:bg-indigo-50 cursor-pointer flex items-center gap-2 font-medium"
                      >
                        <i className={b.startsWith('INV') ? 'ri-file-list-3-line text-indigo-600' : 'ri-barcode-box-line text-amber-600'} />
                        {b}
                        <span className="text-[10px] text-slate-400 ml-auto uppercase">
                          {b.startsWith('INV') ? 'Invoice' : 'Batch'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleSearch()}
                disabled={loading}
                className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm inline-flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-history-line" />}
                Trace Backward
              </button>
            </div>
          </div>

          {/* Empty / Not Found State */}
          {selectedQuery && !loading && !traceData && (
            <div className="bg-white border border-amber-200 rounded-2xl p-8 text-center shadow-sm">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-amber-50 border border-amber-200 mb-4">
                <i className="ri-error-warning-line text-2xl text-amber-500" />
              </div>
              <h3 className="text-lg font-bold text-[#1e293b] mb-1">No Genealogy Records Found</h3>
              <p className="text-sm text-[#64748b]">
                No backward traceability record was found for &quot;{selectedQuery}&quot;.
              </p>
            </div>
          )}

          {traceData && (
            <div className="space-y-6">
              {/* ── CASE A: INVOICE-DRIVEN BACKWARD GENEALOGY ── */}
              {isInvoiceSearch && (
                <>
                  {/* Customer & Invoice Banner */}
                  <div className="bg-white border border-indigo-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-indigo-50">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
                          <i className="ri-file-text-line text-2xl" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                              Sales Invoice
                            </span>
                            <h2 className="text-xl font-bold text-[#1e293b]">{traceData.invoiceNumber}</h2>
                          </div>
                          <p className="text-sm text-slate-600 font-medium mt-0.5">
                            Customer: <span className="font-bold text-slate-900">{traceData.customerName}</span>
                            {traceData.customerPhone && traceData.customerPhone !== '-' && (
                              <span className="text-slate-400 ml-2">({traceData.customerPhone})</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-slate-400 uppercase font-semibold">Invoice Date</span>
                        <div className="text-sm font-bold text-slate-800 mt-0.5">{traceData.invoiceDate}</div>
                        <span className="text-xs text-indigo-600 font-medium">
                          {invoiceBatches.length} Tracked Batch{invoiceBatches.length !== 1 ? 'es' : ''} Sold
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Backward Journey Tree for each batch in this invoice */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                    <h3 className="text-base font-bold text-[#1e293b] mb-6 flex items-center gap-2">
                      <i className="ri-git-merge-line text-indigo-600" />
                      Backward Trace Tree: Invoice &rarr; Batches &rarr; Production &rarr; Raw Materials &rarr; Suppliers
                    </h3>

                    <div className="space-y-8">
                      {invoiceBatches.map((batchItem, bIdx) => (
                        <div key={bIdx} className="bg-slate-50/70 border border-slate-200 rounded-2xl p-5">
                          {/* Batch Sold Header */}
                          <div className="flex items-center justify-between flex-wrap gap-2 pb-4 mb-4 border-b border-slate-200">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 font-bold">
                                <i className="ri-barcode-box-line text-xl" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-bold text-base text-slate-900">{batchItem.batchNumber}</h4>
                                  <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded font-semibold">
                                    {batchItem.sourceType === 'PRODUCTION' ? 'Manufactured' : 'Direct Stock'}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-600 font-medium mt-0.5">
                                  {batchItem.itemName} &bull; Qty Sold on Invoice:{' '}
                                  <span className="font-bold text-indigo-900">
                                    {batchItem.qtySold} {batchItem.unit}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Production Node */}
                          {batchItem.sourceType === 'PRODUCTION' && (
                            <div className="relative pl-6 pb-5 border-l-2 border-indigo-300 ml-4">
                              <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-indigo-600 border-2 border-white shadow-xs" />
                              <div className="bg-white border border-indigo-100 rounded-xl p-3.5 shadow-xs">
                                <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
                                  <div>
                                    <span className="font-bold text-slate-900 text-sm">
                                      Produced in PO: {batchItem.productionOrderNumber}
                                    </span>
                                    <p className="text-slate-500 mt-0.5">
                                      Mfg Date: {batchItem.productionDate} &bull; Operator: {batchItem.operatorName}
                                    </p>
                                  </div>
                                  <span className="px-2.5 py-1 bg-amber-50 text-amber-800 font-semibold rounded-lg border border-amber-200">
                                    {batchItem.rawMaterials.length} Input Raw Material{batchItem.rawMaterials.length !== 1 ? 's' : ''} Used
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Input Raw Materials Tree */}
                          <div className="ml-8 space-y-3">
                            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">
                              Input Raw Materials & Supplier Genealogy:
                            </span>

                            {batchItem.rawMaterials.map((rm) => (
                              <div
                                key={rm.id}
                                className="bg-white border border-amber-200 rounded-xl p-3.5 shadow-xs flex items-center justify-between flex-wrap gap-3"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                                    <i className="ri-inbox-archive-line text-base" />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-sm text-slate-900">{rm.inputBatchNumber}</span>
                                      <span className="text-xs text-slate-600 font-medium">({rm.inputItemName})</span>
                                    </div>
                                    <p className="text-xs text-amber-800 font-medium mt-0.5">
                                      Qty Used: <span className="font-bold">{rm.inputQtyUsed ?? '—'} {rm.inputUnit}</span>
                                    </p>
                                  </div>
                                </div>

                                {/* Supplier Origin */}
                                <div className="text-right text-xs">
                                  <div className="font-bold text-slate-800 flex items-center justify-end gap-1">
                                    <i className="ri-building-line text-blue-500" />
                                    {rm.supplierName}
                                  </div>
                                  <p className="text-slate-500 mt-0.5">
                                    GRN: <span className="font-medium text-slate-700">{rm.grnNumber}</span> &bull; Received:{' '}
                                    {rm.receivedDate}
                                  </p>
                                  {rm.supplierBatchNo && rm.supplierBatchNo !== '-' && (
                                    <p className="text-slate-400 text-[11px]">Vendor Batch: {rm.supplierBatchNo}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ── CASE B: BATCH-DRIVEN BACKWARD GENEALOGY ── */}
              {!isInvoiceSearch && (
                <>
                  {/* Batch Header Card */}
                  <div className="bg-white border border-indigo-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-indigo-50">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
                          <i className="ri-checkbox-circle-fill text-2xl" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                              Target Output Batch
                            </span>
                            <h2 className="text-xl font-bold text-[#1e293b]">{traceData.outputBatchNumber}</h2>
                          </div>
                          <p className="text-sm text-slate-600 font-medium mt-0.5">
                            {traceData.outputItemName} &bull; Output Qty:{' '}
                            <span className="font-bold text-slate-800">
                              {traceData.outputQtyProduced} {traceData.outputUnit}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-slate-400 uppercase font-semibold">Production Order</span>
                        <div className="text-sm font-bold text-slate-800 mt-0.5">
                          {traceData.productionOrderNumber || 'Internal / GRN'}
                        </div>
                        <span className="text-xs text-slate-500">
                          {traceData.productionDate} &bull; {traceData.operatorName}
                        </span>
                      </div>
                    </div>

                    {/* Customers who bought this batch */}
                    {customers.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-slate-100">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                          Customers Invoiced for this Batch:
                        </span>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {customers.map((c, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg text-xs font-medium"
                            >
                              <i className="ri-user-line text-emerald-600" />
                              <span className="font-bold">{c.customerName}</span> ({c.qtySold} sold via {c.invoiceNumber})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Backward Tree */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                    <h3 className="text-base font-bold text-[#1e293b] mb-4 flex items-center gap-2">
                      <i className="ri-git-branch-line text-indigo-600" />
                      Trace Tree &rarr; Input Raw Materials Used
                    </h3>

                    {directLinks.length === 0 ? (
                      <div className="p-6 bg-slate-50 rounded-xl text-center text-sm text-slate-500">
                        No raw material consumption links registered for this batch.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {directLinks.map((link) => (
                          <div
                            key={link.id}
                            className="bg-amber-50/40 border border-amber-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 text-amber-800 shrink-0">
                                <i className="ri-barcode-box-line text-lg" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-bold text-sm text-slate-900">{link.inputBatchNumber}</h4>
                                  <span className="text-xs text-slate-600 font-medium">({link.inputItemName})</span>
                                </div>
                                <p className="text-xs text-amber-800 font-medium mt-0.5">
                                  Used: <span className="font-bold">{link.inputQtyUsed ?? '—'} {link.inputUnit}</span> from this batch
                                </p>
                              </div>
                            </div>

                            <div className="text-right text-xs">
                              <div className="font-bold text-slate-800 flex items-center justify-end gap-1">
                                <i className="ri-building-line text-blue-500" />
                                {link.supplierName || 'Internal / Stock'}
                              </div>
                              <p className="text-slate-500 mt-0.5">
                                GRN: <span className="font-medium text-slate-700">{link.grnNumber}</span> &bull; Date:{' '}
                                {link.receivedDate}
                              </p>
                              {link.supplierBatchNo && link.supplierBatchNo !== '-' && (
                                <p className="text-slate-400 text-[11px]">Vendor Batch: {link.supplierBatchNo}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Root Materials Table */}
                  {directLinks.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                        <h3 className="text-sm font-bold text-[#1e293b] flex items-center gap-2">
                          <i className="ri-file-list-3-line text-slate-500" />
                          Root Materials Summary Table
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50/70 border-b border-slate-200">
                              <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">
                                Input Batch #
                              </th>
                              <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">
                                Material Name
                              </th>
                              <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">
                                Qty Used
                              </th>
                              <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">
                                Supplier / Vendor
                              </th>
                              <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">
                                GRN Reference
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {directLinks.map((link) => (
                              <tr key={link.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                                <td className="px-4 py-3 font-bold text-amber-800">{link.inputBatchNumber}</td>
                                <td className="px-4 py-3 font-medium text-slate-800">{link.inputItemName}</td>
                                <td className="px-4 py-3 font-medium text-slate-800">
                                  {link.inputQtyUsed ?? '—'} {link.inputUnit}
                                </td>
                                <td className="px-4 py-3 text-xs text-slate-600 font-medium">
                                  {link.supplierName || 'Internal / Stock'}
                                </td>
                                <td className="px-4 py-3 text-xs text-slate-600 font-medium">
                                  {link.grnNumber || 'Direct Stock'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}